import React, { Fragment, useState } from "react"

import { useTranslation } from "react-i18next"
import { useQueries, useQuery } from "react-query"
import { useDispatch } from "react-redux"
import { useNavigate, useParams } from "react-router-dom"

import { Card, Grid, useMediaQuery, useTheme } from "@mui/material"
import { Button, Skeleton, Tag, Tooltip, Typography } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"
import moment from "moment"

import { KonanAvatarComponent } from "../../../components/core/KonanAvatar"
import { KonanLogo } from "../../../components/icons/KonanLogo"
import { ModelType } from "../../../components/status/ModelType"
import { KonanAPI } from "../../../services/KonanAPI"
import { RetrainingJob } from "../../../types/generated/api/RetrainingJob"
import type { TrainingDataUpload } from "../../../types/generated/api/TrainingDataUpload"
import { uniqueJobName } from "../../../utils/deploymentDetailsHelpers"
import { KonanTimeHelper } from "../../../utils/genericHelpers"
import { setCurrentModel } from "../../Models"
import { RetrainingLogsView } from "./RetrainingLogs"
import { TrainingReportDialog } from "./TrainingReportDialog"

import styles from "../TrainingJobs.module.scss"

function EvaluationDataCardLoader(): React.ReactElement {
  return (
    <Grid
      container
      direction="column"
      justifyContent="space-around"
      alignItems="flex-start"
      className={styles.dataCard}
    >
      <Skeleton height={10} width={"100%"} />
      <Skeleton height={10} width={"100%"} />
    </Grid>
  )
}

type EvalProps = {
  title: string
  trainValue: number
  testValue: number
}

function EvaluationDataCard(props: Readonly<EvalProps>): React.ReactElement {
  const { title, trainValue, testValue } = props

  const metricValue = `${trainValue?.toFixed(2)} / ${testValue?.toFixed(2)}`

  return (
    <Grid
      container
      direction="column"
      justifyContent="space-between"
      alignItems="flex-start"
      className={styles.dataCard}
    >
      {/* Title */}
      <Typography variant="p" noWrap className={styles.trainingText}>
        {title}
      </Typography>

      {/* Value */}
      <Typography variant="p" noWrap className={styles.trainingText}>
        {typeof trainValue === "number" && typeof testValue === "number" ? metricValue : trainValue / testValue}
      </Typography>
    </Grid>
  )
}

type MetricType = {
  metric_name: string
  metric_value: number
}

type Props = {
  parentModel: string
  startedAt: string
  firstPredictionDate: string
  lastPredictionDate: string
  createdModelName: string
  endedAt: string
  trainingDataId: string
  resultingModelId: string
  isLoading?: boolean
  status?: RetrainingJob.status
  createdAt: string
  createdModelStatus?: string
  evalMetrics: {
    test: MetricType[]
    train: MetricType[]
  }
  uuid: string
  points: number | undefined
  projectType?: string
  /** Used to guarantee that avatars are unique per user. */
  userName?: string
  duration?: string
  dataPoints?: string
  trainingOrRetraining: string
}

export function SuccessfulTrainingJobCard(props: Readonly<Props>): React.ReactElement {
  const { t } = useTranslation("TrainingJob")
  const {
    isLoading,
    status,
    startedAt,
    createdAt,
    createdModelName,
    createdModelStatus,
    parentModel,
    userName,
    firstPredictionDate,
    lastPredictionDate,
    dataPoints,
    trainingDataId,
    endedAt,
    projectType,
    uuid,
    duration,
    trainingOrRetraining,
    points,
    evalMetrics,
    resultingModelId,
  } = props
  const { id: projectId } = useParams<{ id: string }>()

  const dispatch = useDispatch()

  const theme = useTheme()
  const navigate = useNavigate()

  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"))

  const [openRetrainingLogs, setOpenRetrainingLogs] = useState(false)
  const [trainingReportDialogOpen, setTrainingReportDialogOpen] = useState(false)

  const konanTime = new KonanTimeHelper()
  const isTrainingType = trainingOrRetraining === "training-automl" || trainingOrRetraining === "training-prebuilt"

  const [parentModelRes, resultingModelRes] = useQueries(
    [parentModel, resultingModelId].map((modelId) => ({
      queryKey: ["model", modelId],
      queryFn: () => KonanAPI.fetchModel(modelId),
      enabled: !!modelId,
    })),
  )

  const { data: trainingData, isLoading: isTrainingDataLoading } = useQuery<
    AxiosResponse<TrainingDataUpload>,
    AxiosError
  >(
    ["training-data", projectId, trainingDataId],
    () => KonanAPI.fetchSingleTrainingData(projectId as string, trainingDataId),
    { enabled: projectId != null && trainingDataId != null },
  )

  // fetch threshold metrics
  const { data: thresholdMetricsData, isLoading: isThresholdMetricsLoading } = useQuery<AxiosResponse>(
    ["threshold-metrics", resultingModelId],
    () => KonanAPI.fetchThresholdWithMetrics({ projectId: projectId as string, modelUUID: resultingModelId }),
    {
      enabled: !!projectId && !!resultingModelId && trainingOrRetraining === "training-automl",
    },
  )

  const handleLogsClose = (): void => {
    setOpenRetrainingLogs(false)
  }

  return (
    <Fragment>
      {openRetrainingLogs && (
        <RetrainingLogsView
          open={openRetrainingLogs}
          onClose={handleLogsClose}
          projectId={projectId as string}
          retrainingUUID={uuid}
          jobStatus={status}
          trainingJobType={trainingOrRetraining}
          trainingName={uniqueJobName(uuid, createdAt, trainingOrRetraining)}
          isAutoMl={trainingOrRetraining === "training-automl"}
        />
      )}

      {trainingReportDialogOpen && (
        <TrainingReportDialog
          TrainingDataName={trainingData?.data?.name ? trainingData.data?.name : ""}
          projectId={projectId as string}
          onClose={() => setTrainingReportDialogOpen(false)}
          open
          uuid={uuid}
          createdModelStatus={createdModelStatus}
          createdModelName={createdModelName}
          resultingModel={resultingModelId}
          dataSplit={dataPoints}
          points={points}
          evalMetrics={evalMetrics}
          isAutoMl={trainingOrRetraining === "training-automl"}
        />
      )}

      <Card className="card-box-shadow">
        <Grid container direction="column" className={styles.headerContainer}>
          <Grid item xs={12} container justifyContent="space-between" alignItems="flex-start">
            <Grid item xs={12} container direction="row">
              <Grid item>
                <Tag color="green" size="small">
                  {t("labels.success")}
                </Tag>
              </Grid>
              <Grid item direction="column" justifyContent="center">
                <Typography variant="span-regular" className={styles.durationText} noWrap>
                  {!isLoading && duration ? `${t("labels.lasted")} ${konanTime.timeParser(duration)}` : <br />}
                </Typography>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h3-bold" noWrap className={styles.headerName}>
              {uniqueJobName(uuid, createdAt, trainingOrRetraining)}
            </Typography>
          </Grid>
          <Grid container justifyContent="space-between" wrap="nowrap" spacing={1}>
            <Grid
              container
              justifyContent="space-between"
              direction="row"
              item
              alignItems="flex-start"
              className={styles.avatarContainer}
              sm={isTrainingType ? 7 : 8}
              xs={9}
            >
              <KonanAvatarComponent createdBy={userName ?? ""} title={userName} createdAt={createdAt} />
            </Grid>
            <Grid item sm={isTrainingType ? 5 : 4} xs={3} container className={styles.buttonsContainer}>
              {isTrainingType ? (
                <Button onClick={() => setTrainingReportDialogOpen(true)} size="small" variant="secondary">
                  {fullScreen ? t("cards.successful.viewReport") : t("dialogs.trainingReport.title")}
                </Button>
              ) : (
                <Grid item direction="row">
                  <Button onClick={() => setOpenRetrainingLogs(true)} size="small" variant="secondary">
                    {fullScreen ? t("cards.successful.viewLogs") : t("cards.successful.viewLogs")}
                  </Button>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
        <Grid
          container
          direction="column"
          className={trainingOrRetraining !== "training-automl" ? styles.bodyContainer : styles.bodyContainerAutoml}
        >
          <Grid container item direction="row" alignItems="flex-end">
            <Grid container item direction="row">
              <Grid item xs={12} sm={9}>
                <Grid container direction="row" justifyContent="flex-start" alignItems="flex-end">
                  <Typography
                    variant="label-bold"
                    tooltip={
                      isTrainingType
                        ? t("cards.successful.trainedModelDescription")
                        : t("cards.successful.originalVersionDescription")
                    }
                    tooltipVerticalAlign="middle"
                  >
                    {t("cards.successful.sourceModel")}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Grid>
            <Grid container className={styles.sourceModelSection}>
              {isTrainingType ? (
                <Fragment>
                  <Grid item marginBottom={1}>
                    <Typography variant="span-bold">{projectType}</Typography>
                  </Grid>
                  <Grid item>
                    <KonanLogo logoWidth={115} />
                  </Grid>
                </Fragment>
              ) : (
                <Grid item container direction="column" className={styles.sourceModel} sx={{ width: "100%" }}>
                  {!parentModelRes?.isLoading && parentModelRes?.data && (
                    <Fragment>
                      <Grid item>
                        <ModelType modelState={parentModelRes.data.data?.state} size="small" />
                      </Grid>
                      <Grid
                        item
                        onClick={() => {
                          navigate(`/projects/${projectId}/decision-engines`)
                          dispatch(setCurrentModel(parentModelRes.data.data))
                        }}
                        sx={{ width: "100%" }}
                      >
                        <Typography variant="p" noWrap className={styles.linkText} color="important" variantColor={2}>
                          {parentModelRes.data.data?.name}
                        </Typography>
                      </Grid>

                      <Grid className={styles.dateContainer} item>
                        <Tooltip
                          title={moment(
                            parentModelRes.data.data?.created_at ? parentModelRes.data.data?.created_at : "",
                          ).format("DD/MM/YYYY, h:mm A")}
                          placement="right"
                        >
                          <Typography variant="label-regular" className={styles.date}>
                            {moment(
                              parentModelRes.data.data?.created_at ? parentModelRes.data.data?.created_at : "",
                            ).fromNow()}
                          </Typography>
                        </Tooltip>
                      </Grid>
                    </Fragment>
                  )}
                  {parentModelRes?.isLoading && (
                    <Grid
                      container
                      item
                      xs={12}
                      direction="column"
                      justifyContent="flex-start"
                      alignItems="flex-start"
                      flexWrap="nowrap"
                    >
                      <Grid container direction="column" className={"generatedModelCard"}>
                        <Grid item>
                          <Skeleton variant="rectangular" width={"40%"} height={10} />
                        </Grid>
                        <Grid item>
                          <Skeleton variant="rectangular" width={"70%"} height={10} />
                        </Grid>
                        <Grid item>
                          <Skeleton variant="rectangular" width={"40%"} height={10} />
                        </Grid>
                      </Grid>
                    </Grid>
                  )}
                  {parentModelRes?.isError && (
                    <Grid container direction="row" justifyContent="center" item xs={12} py={3}>
                      <Typography variant="label-regular" align="center" className={styles.emptyPredictions}>
                        {t("cards.successful.unableToFetchSourceModel")}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              )}
            </Grid>
          </Grid>
          <Grid container item direction="row" className={styles.twoBoxesContainer}>
            <div className={styles.testWidthLeft}>
              <Typography
                variant="label-bold"
                tooltip={t("tooltips.startDatetime", {
                  type: isTrainingType ? t("cards.successful.type.training") : t("cards.successful.type.retraining"),
                })}
                tooltipVerticalAlign="middle"
                className={styles.labelColor}
              >
                {t("labels.started")}
              </Typography>
              <Grid item className={styles.idbox} sx={{ width: "100px" }}>
                <Typography variant="p" noWrap className={styles.textIdBox}>
                  {moment(startedAt).format("DD/MM/YYYY, h:mm:ss A")}
                </Typography>
              </Grid>
            </div>
            <div className={styles.testWidthRight}>
              <Typography
                variant="label-bold"
                tooltip={t("tooltips.endDatetime", {
                  type: isTrainingType ? t("cards.successful.type.training") : t("cards.successful.type.retraining"),
                })}
                tooltipVerticalAlign="middle"
                className={styles.labelColor}
              >
                {t("labels.ended")}
              </Typography>
              <Grid container className={styles.idbox}>
                <Typography noWrap variant="p" className={styles.textIdBox}>
                  {moment(endedAt).format("DD/MM/YYYY, h:mm:ss A")}
                </Typography>
              </Grid>
            </div>
          </Grid>
          <Grid
            container
            item
            direction="row"
            className={trainingOrRetraining !== "training-automl" ? styles.twoBoxesContainer : ""}
          >
            <div className={styles.testWidthLeft}>
              {trainingOrRetraining === "retraining" ? (
                <Fragment>
                  <Typography
                    variant="label-bold"
                    tooltip={t("tooltips.servingDataRange")}
                    tooltipVerticalAlign="middle"
                    className={styles.labelColor}
                  >
                    {t("labels.servingData")}
                  </Typography>
                  <Grid container className={styles.idbox}>
                    <Typography variant="p" noWrap className={styles.textIdBox}>
                      {firstPredictionDate && lastPredictionDate
                        ? `${moment(firstPredictionDate).format("DD/MM/YYYY")} -
                      ${moment(lastPredictionDate).format("DD/MM/YYYY")}`
                        : t("common.na")}
                    </Typography>
                  </Grid>
                </Fragment>
              ) : (
                <Fragment>
                  <Typography
                    variant="label-bold"
                    tooltip={t("tooltips.trainingDataUsed")}
                    tooltipVerticalAlign="middle"
                    className={styles.labelColor}
                  >
                    {t("labels.trainingData")}
                  </Typography>
                  <Grid container className={styles.idbox}>
                    {isTrainingDataLoading ? (
                      <Grid item xs={12}>
                        <Typography variant="p">
                          <Skeleton variant="rectangular" width={"100%"} height={10} />
                        </Typography>
                      </Grid>
                    ) : (
                      <Typography variant="p" noWrap className={styles.textIdBox}>
                        {!isTrainingDataLoading && trainingData?.data?.name
                          ? trainingData.data?.name
                          : !isTrainingDataLoading && trainingData?.data?.uuid && !trainingData.data?.name
                            ? "data.csv"
                            : !isTrainingDataLoading && t("common.na")}
                      </Typography>
                    )}
                  </Grid>
                </Fragment>
              )}
            </div>
            <div className={styles.testWidthRight}>
              <Typography
                variant="label-bold"
                tooltip={t("tooltips.dataSplitPercentage")}
                tooltipVerticalAlign="middle"
                className={styles.labelColor}
              >
                {t("labels.dataSplit")}
              </Typography>
              <Grid container className={styles.idbox}>
                {isLoading || isThresholdMetricsLoading ? (
                  <Skeleton variant="rectangular" width="100%" height={20} />
                ) : (status === RetrainingJob.status.SUCCEEDED && dataPoints) ||
                  !!thresholdMetricsData?.data?.metrics?.split?.train ? (
                  <Typography variant="p" noWrap className={styles.textIdBox}>
                    {trainingOrRetraining === "training-automl"
                      ? `${thresholdMetricsData?.data?.metrics?.split?.train * 100}% / ${thresholdMetricsData?.data?.metrics?.split?.test * 100}%`
                      : dataPoints}
                  </Typography>
                ) : (
                  <Typography variant="p" noWrap>
                    {t("common.na")}
                  </Typography>
                )}
              </Grid>
            </div>
          </Grid>
          {trainingOrRetraining !== "training-automl" && (
            <Fragment>
              <Grid container item direction="row" style={{ paddingLeft: 0 }}>
                <Grid container item direction="row">
                  <Grid item xs={12} sm={9}>
                    <Grid container direction="row" justifyContent="flex-start" alignItems="flex-start">
                      <Typography
                        variant="label-bold"
                        display="block"
                        tooltip={
                          isTrainingType
                            ? t("cards.successful.performanceDescription.trained")
                            : t("cards.successful.performanceDescription.retrained")
                        }
                        tooltipVerticalAlign="text-bottom"
                        noWrap
                      >
                        {t("labels.evaluationMetricsTrainTest")}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
              {!evalMetrics && !isLoading ? (
                <Grid>
                  <Grid
                    className={styles.addTrainingSection}
                    container
                    direction="row"
                    justifyContent="space-between"
                    item
                    xs={12}
                    marginBottom={4}
                  >
                    <Typography variant="label-regular" align="center" className={styles.emptyPredictions}>
                      {t("common.noEvaluationTests")}
                    </Typography>
                  </Grid>
                </Grid>
              ) : (
                <Grid container direction="row" spacing={1} className={styles.horizontalScrolling}>
                  {isLoading &&
                    [1, 2, 3].map((_, index: number) => (
                      <Grid item xs={4} key={index}>
                        <EvaluationDataCardLoader />
                      </Grid>
                    ))}

                  {!isLoading &&
                    evalMetrics &&
                    Boolean(evalMetrics?.train?.length) &&
                    evalMetrics?.train?.map((ev: { metric_name: string; metric_value: number }, idx) => (
                      <Grid
                        item
                        xs={evalMetrics?.train?.length === 1 ? 12 : evalMetrics?.train?.length === 2 ? 6 : 4}
                        className={styles.evalCard}
                        key={`${ev.metric_name}-${ev.metric_value}`}
                      >
                        <EvaluationDataCard
                          title={ev?.metric_name}
                          trainValue={ev?.metric_value}
                          testValue={evalMetrics?.test[idx]?.metric_value ?? 0}
                        />
                      </Grid>
                    ))}
                </Grid>
              )}
            </Fragment>
          )}

          <Grid container item direction="row" alignItems="flex-end">
            <Typography
              variant="label-bold"
              tooltip={t("tooltips.newModelVersion", {
                type: isTrainingType ? t("cards.successful.type.training") : t("cards.successful.type.retraining"),
              })}
              tooltipVerticalAlign="middle"
            >
              {t("labels.generatedModel")}
            </Typography>
          </Grid>

          {createdModelStatus &&
            (!resultingModelRes?.isLoading ? (
              <Grid container className={styles.sourceModelSectionGenerated}>
                <Grid item container direction="column" className={styles.sourceModel}>
                  <Fragment>
                    <Grid item>
                      <ModelType modelState={createdModelStatus} size="small" />
                    </Grid>
                    <Grid
                      item
                      onClick={() => {
                        navigate(`/projects/${projectId}/decision-engines`)
                        dispatch(setCurrentModel(resultingModelRes?.data?.data))
                      }}
                      sx={{ width: "100%" }}
                    >
                      <Typography variant="p" noWrap className={styles.linkText} color="important" variantColor={2}>
                        {createdModelName}
                      </Typography>
                    </Grid>

                    <Grid item className={styles.dateContainer}>
                      <Tooltip title={moment(endedAt).format("DD/MM/YYYY, h:mm A")} placement="right">
                        <Typography variant="label-regular" className={styles.date}>
                          {moment(endedAt).fromNow()}
                        </Typography>
                      </Tooltip>
                    </Grid>
                  </Fragment>
                </Grid>
              </Grid>
            ) : (
              <Grid
                container
                direction="column"
                justifyContent="space-around"
                alignItems="flex-start"
                className={styles.sourceModelSectionLoader}
                item
              >
                <Skeleton width={"100%"} height={80} />
              </Grid>
            ))}
        </Grid>
      </Card>
    </Fragment>
  )
}
